import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  FaBuilding,
  FaHome,
  FaUser,
  FaWindowClose,
  FaSpinner,
  FaFilter,
  FaStar,
  FaShare,
  FaBed,
  FaBath,
  FaCompass,
  FaLeaf,
  FaRulerCombined,
  FaArrowLeft,
} from "react-icons/fa";
import { VscChromeClose } from "react-icons/vsc";
import HeartIcon from "../Icons/HeartIcon";
import Pagination from "../Pagination/Pagination";
import FilterDialog from "../FilterDialog/FilterDialog";
import SortOptions from "../SortOptions/SortOptions";
import UnitCard from "./UnitCard";
import SearchProjectCard from "./SearchProjectCard";
import "./SearchResults.css";
import { useLanguage } from "../../App";
import { getFavorites } from "../../services/api";
import { calculateCombinedBounds } from "../../utils/mapBoundsCalculator";

const SearchResults = forwardRef(
  (
    {
      searchResults,
      isVisible,
      isLoading = false,
      searchType = "all", // 'all', 'projects', 'units', 'developers'
      onClose,
      onProjectSelect,
      onUnitSelect,
      onDeveloperSelect,
      onToggleFavorite,
      onReservation,
      onShare,
      onPageChange,
      onItemsPerPageChange,
      onFiltersChange,
      onSortChange,
      filters = {},
      searchSort = {},
      isFavoritesMode = false, // New prop to indicate favorites mode
      favoritesData = null, // New prop for favorites data
      onMapBoundsUpdate = null, // Callback to update map bounds
      customTitle = null,      // Optional custom title
      hasHistory = false,      // Indicates if there are previous search results
      onBackToHistory = null,  // Callback to go back to previous results
      isUnitBackgroundLoading = false, // Indicates if single unit is loading in background
      hideUnitsTab = false,
      favoriteLoadingIds = {}, // Map of parcel id -> true while a favorite toggle is in flight
    },
    ref
  ) => {
    const { language } = useLanguage();
    const [activeTab, setActiveTab] = useState("units");
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const containerRef = useRef(null);

    // Favorites state
    const [favorites, setFavorites] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [favoritesError, setFavoritesError] = useState(null);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        minimize: () => setIsMinimized(true),
        expand: () => setIsMinimized(false),
        toggleMinimize: () => setIsMinimized((prev) => !prev),
        setActiveTab: (tab) => setActiveTab(tab),
        loadFavorites: loadFavorites,
        loadMapBounds: loadMapBounds,
        getActiveTab: () => activeTab,
        isMinimized: () => isMinimized,
      }),
      [isMinimized, activeTab]
    );

    // Always expand results on new search
    useEffect(() => {
      setIsMinimized(false);
    }, [searchResults]);

    useEffect(() => {
      if (hideUnitsTab && activeTab === "units") {
        setActiveTab("projects");
      }
    }, [hideUnitsTab, activeTab]);

    // Load favorites when in favorites mode
    useEffect(() => {
      if (isFavoritesMode && isVisible) {
        loadFavorites();
      }
    }, [isFavoritesMode, isVisible]);

    // Update map bounds when results are displayed
    const loadMapBounds = async (overrideData = null) => {
      // Small delay so the results overlay can settle before the map animation starts
      await new Promise((resolve) => setTimeout(resolve, 300));

      let units = [];
      let projects = [];

      if (overrideData) {
        units = overrideData.units || [];
        projects = overrideData.projects || [];
      } else if (isFavoritesMode) {
        units = favorites;
      } else if (searchResults) {
        units = searchResults.units || [];
        projects = searchResults.projects || [];
      }

      const bounds = calculateCombinedBounds(units, projects);

      if (bounds && onMapBoundsUpdate) {
        onMapBoundsUpdate(bounds);
      }
    };

    const loadFavorites = async () => {
      try {
        setFavoritesLoading(true);
        setFavoritesError(null);
        const response = await getFavorites();
        const favoritesData = response.data.data || [];
        setFavorites(favoritesData);
        loadMapBounds({ units: favoritesData });
      } catch (err) {
        console.error("Error loading favorites:", err);
        setFavoritesError(
          language === "ar"
            ? "حدث خطأ في تحميل المفضلة"
            : "Error loading favorites"
        );
      } finally {
        setFavoritesLoading(false);
      }
    };

    // Set active tab based on search type or favorites mode
    useEffect(() => {
      if (isFavoritesMode) {
        setActiveTab("units"); // Always show units tab in favorites mode
      } else if (searchType === "projects") {
        setActiveTab("projects");
      } else if (searchType === "units") {
        setActiveTab("units");
      } else if (searchType === "developers") {
        setActiveTab("developers");
      } else if (searchType === "all") {
        // For 'all', keep current tab or default to units
        if (!activeTab) {
          setActiveTab("units");
        }
      }
    }, [searchType, isFavoritesMode]);

    // Handle click outside to minimize
    useEffect(() => {
      const handleClickOutside = (event) => {
        // Only minimize if visible and not already minimized
        if (isVisible && !isMinimized && containerRef.current) {
          // Ignore clicks inside the project details panel or any open modal/overlay,
          // otherwise opening the photo gallery / video / brochure etc. would minimize the results.
          if (event.target.closest(
            '.project-details-panel, .photo-gallery-modal, .video-modal-overlay, .video-modal, .pdf-viewer-modal-overlay, .pdf-viewer-modal, .iframe-modal-overlay, .iframe-modal, .share-modal, .modal, .MuiDialog-root, .MuiModal-root'
          )) {
            return;
          }
          // Check if click is outside the container
          if (!containerRef.current.contains(event.target)) {
            setIsMinimized(true);
          }
        }
      };

      // Add event listener
      if (isVisible && !isMinimized) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      // Cleanup
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isVisible, isMinimized]);

    // Helper function to count active filters
    const getActiveFiltersCount = () => {
      const currentFilters = filters[activeTab] || {};
      return Object.values(currentFilters).filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (Array.isArray(value) ? value.length > 0 : true)
      ).length;
    };

    if (!isVisible) return null;

    // Handle favorites mode vs search results mode
    let projects = [];
    let units = [];
    let developers = [];
    let summary = {};
    let pagination = {};

    if (isFavoritesMode) {
      // In favorites mode, use favorites data
      units = favorites;
      summary = { total_units: favorites.length };
      pagination = {
        total: favorites.length,
        current_page: 1,
        per_page: favorites.length,
      };
    } else {
      // In search mode, use search results
      if (!searchResults) return null;
      const searchData = searchResults;
      projects = searchData.projects || [];
      units = searchData.units || [];
      developers = searchData.developers || [];
      summary = searchData.summary || {};
      pagination = searchData.pagination || {};
    }

    const isSingleView = Boolean(customTitle || hasHistory);
    
    const allTabs = [
      {
        id: "units",
        label: language === "ar" 
          ? (isSingleView ? "الوحدة" : "الوحدات") 
          : (isSingleView ? "Unit" : "Units"),
        icon: FaHome,
        count: summary.total_units || 0,
        data: units,
      },
      {
        id: "projects",
        label: language === "ar" 
          ? (isSingleView ? "المشروع" : "المشاريع") 
          : (isSingleView ? "Project" : "Projects"),
        icon: FaBuilding,
        count: summary.total_projects || 0,
        data: projects,
      },
      {
        id: "developers",
        label: language === "ar" 
          ? (isSingleView ? "المطور" : "المطورين") 
          : (isSingleView ? "Developer" : "Developers"),
        icon: FaUser,
        count: summary.total_developers || 0,
        data: developers,
      },
    ];

    // Filter tabs based on search type or favorites mode
    let tabs = isFavoritesMode
      ? allTabs.filter((tab) => tab.id === "units") // Only show units tab in favorites mode
      : searchType === "all"
      ? allTabs
      : allTabs.filter((tab) => tab.id === searchType);

    if (hideUnitsTab) {
      tabs = tabs.filter((tab) => tab.id !== "units");
    }

    const handleProjectClick = (project) => {
      if (onProjectSelect) {
        onProjectSelect(project);
      }
      setIsMinimized(true);
    };

    const handleUnitClick = (unit) => {
      if (onUnitSelect) {
        onUnitSelect(unit);
      }
      setIsMinimized(true);
    };

    const handleDeveloperClick = (developer) => {
      if (onDeveloperSelect) {
        onDeveloperSelect(developer);
      }
      setIsMinimized(true);
    };

    const handleMinimizeToggle = () => {
      setIsMinimized(!isMinimized);
    };

    const formatPrice = (price) => {
      if (!price) return language === "ar" ? "غير محدد" : "Not specified";
      return new Intl.NumberFormat("en-US").format(price);
    };

    const renderProjectCard = (project) => (
      <SearchProjectCard
        key={project.id}
        project={project}
        language={language}
        onProjectSelect={handleProjectClick}
      />
    );

    const renderUnitCard = (unit) => (
      <UnitCard
        key={unit.id}
        unit={unit}
        language={language}
        onUnitSelect={handleUnitClick}
        onToggleFavorite={onToggleFavorite}
        onReservation={onReservation}
        onShare={onShare}
        isLoadingFavorite={!!favoriteLoadingIds[unit.id]}
      />
    );

    const renderDeveloperCard = (developer) => (
      <div key={developer.name} className="property-card-container">
        <div
          className="property-card developer-card"
          onClick={() => handleDeveloperClick(developer)}
        >
          {/* Left Side - Details */}
          <div className="card-left">
            {/* Developer Header */}
            <div className="price-header">
              <div className="price-info">
                <span>{language === "ar" ? "مطور" : "Developer"}</span>
                <span className="price-amount-bold">{developer.name}</span>
              </div>
              <div className="availability-badge developer-badge">
                <span>{language === "ar" ? "مطور" : "Developer"}</span>
              </div>
            </div>

            <hr className="hr-divider" />

            {/* Projects Count */}
            <div className="price-section">
              <span className="text-bold">
                {language === "ar" ? "عدد المشاريع" : "Projects Count"}
              </span>
              <span className="price-amount-bold">
                {developer.projects_count || 0}
              </span>
              <span className="price-amount-margin">
                {language === "ar" ? "مشروع" : "Projects"}
              </span>
            </div>

            <hr className="hr-divider" />

            {/* Location */}
            <div className="monthly-section">
              <span>{language === "ar" ? "الموقع" : "Location"}</span>
              <span className="price-amount-bold">
                {developer.city
                  ? language === "ar"
                    ? developer.city.name_ar
                    : developer.city.name_en
                  : language === "ar"
                  ? "غير محدد"
                  : "Not specified"}
              </span>
            </div>

            <hr className="hr-divider" />

            {/* Rating */}
            <div className="total-price-section text-small">
              <span>{language === "ar" ? "التقييم" : "Rating"}</span>
              <span className="price-amount-bold">4.5</span>
              <span>{language === "ar" ? "نجوم" : "Stars"}</span>
            </div>

            {/* Features */}
            {/* <div className="features-section">
            <div className="feature-item">
              <FaUser className="feature-icon" />
              <p className="feature-text">{language === 'ar' ? 'مطور' : 'Dev'}</p>
            </div>
            <div className="feature-item border-not-first">
              <FaBuilding className="feature-icon" />
              <p className="feature-text">{developer.projects_count || 0}</p>
            </div>
            <div className="feature-item border-not-first">
              <FaMapMarkerAlt className="feature-icon" />
              <p className="feature-text">{language === 'ar' ? 'موقع' : 'Location'}</p>
            </div>
            <div className="feature-item border-not-first">
              <FaStar className="feature-icon" />
              <p className="feature-text">4.5</p>
            </div>
          </div> */}

            {/* View Button */}
            <button
              className="reserve-button developer-button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeveloperClick(developer);
              }}
            >
              {language === "ar" ? "عرض المطور" : "View Developer"}
            </button>
          </div>

          {/* Right Side - Image */}
          <div
            className="card-right developer-right"
            style={{
              backgroundImage: developer.logo_url
                ? `url(${developer.logo_url})`
                : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            {/* Top Section */}
            <div className="top-section">
              <div className="logo-container">
                {developer.logo_url && (
                  <img
                    src={developer.logo_url}
                    alt="Developer Logo"
                    className="developer-logo"
                  />
                )}
                <div className="rating-badge">
                  <FaStar className="star-icon" />
                  <span>4.5</span>
                </div>
              </div>
              {/* <div className="action-buttons">
              <button
                className="share-button"
                onClick={(e) => e.stopPropagation()}
              >
                <FaShare className="share-icon" />
              </button>
            </div> */}
            </div>

            {/* Location Info */}
            <div className="location-infoSearch">
              <p>{developer.name}</p>
              <p className="location-date">
                {developer.city
                  ? language === "ar"
                    ? developer.city.name_ar
                    : developer.city.name_en
                  : language === "ar"
                  ? "غير محدد"
                  : "Not specified"}
              </p>
            </div>

            {/* Amenities */}
            {/* <div className="amenities-container">
            <div className="amenity-icon">
              <FaUser className="amenity-icon-img" />
            </div>
            <div className="amenity-icon">
              <FaBuilding className="amenity-icon-img" />
            </div>
            <div className="amenity-icon">
              <FaMapMarkerAlt className="amenity-icon-img" />
            </div>
            <div className="amenity-icon">
              <FaStar className="amenity-icon-img" />
            </div>
          </div> */}
          </div>
        </div>
      </div>
    );

    return (
      <div
        className={`search-results-overlay ${isMinimized ? "minimized" : ""}`}
      >
        <div className="search-results-container" ref={containerRef}>
          <div className="drag-handle-cont" onClick={handleMinimizeToggle}>
            {" "}
            <div className="drag-handle"></div>
          </div>

          {/* Header Section with Stats */}
          <div className="search-results-header" onClick={handleMinimizeToggle}>
            <div className="header-top-bar">
              <button className="back-arrow" onClick={onClose}>
                <VscChromeClose />
              </button>

              <div className="header-stats" style={{flex: 1, textAlign: 'center'}}>
                <span>
                  {customTitle
                    ? customTitle
                    : isFavoritesMode
                    ? language === "ar"
                      ? "المفضلة"
                      : "Favorites"
                    : language === "ar"
                    ? "نتائج البحث"
                    : "Search Results"}
                </span>

                {isUnitBackgroundLoading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '10px', marginRight: '10px', fontSize: '14px', color: '#ff6b35' }}>
                    <FaSpinner className="spinner-animation" style={{ animation: 'spin 1s linear infinite', marginRight: '5px' }} />
                    <style>{`
                      @keyframes spin { 100% { transform: rotate(360deg); } }
                    `}</style>
                    {language === 'ar' ? 'يتم التحديث...' : 'Updating...'}
                  </span>
                )}
              </div>
              {isMinimized && (
                <button
                  className="expand-button"
                  onClick={handleMinimizeToggle}
                >
                  {language === "ar" ? "توسيع" : "Expand"}
                </button>
              )}
              {!isMinimized && (
                <button
                  className="expand-button"
                  onClick={handleMinimizeToggle}
                  style={{ visibility: "hidden" }}
                >
                  {language === "ar" ? "توسيع" : "Expand"}
                </button>
              )}
              {hasHistory && (
                <button
                  className="expand-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onBackToHistory) onBackToHistory();
                  }}
                  style={{ visibility: 'visible' }}
                >
                  {language === 'ar' ? 'رجوع للنتائج' : 'Back to Results'}
                  {/* <FaArrowLeft style={{ transform: language === 'ar' ? 'rotate(180deg)' : 'none', marginRight: '4px'   , marginLeft: '4px'}} /> */}
                </button>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="main-content">
            {/* Tabs Navigation - Hide in favorites mode */}
            {!isFavoritesMode && (
              <div className="search-results-tabs">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      className={`tab-button ${
                        activeTab === tab.id ? "active" : ""
                      }`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {/* <IconComponent className="tab-icon" /> */}
                      <span className="tab-label">{tab.label}</span>
                      <span className="tab-count">({tab.count})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filters and Sort Bar */}
            {/* <div className={`search-controls-bar ${isLoading || isApplyingFilters ? 'loading' : ''}`}>
            <div className="search-controls-left">
              <button
                className={`filter-toggle-btn ${getActiveFiltersCount() > 0 ? 'active' : ''}`}
                onClick={() => setShowFilterDialog(true)}
                disabled={isLoading || isApplyingFilters}
              >
                {isApplyingFilters ? (
                  <FaSpinner className="spinner" />
                ) : (
                  <FaFilter />
                )}
                <span>{language === 'ar' ? 'الفلاتر' : 'Filters'}</span>
                {getActiveFiltersCount() > 0 && (
                  <span className="filter-count">({getActiveFiltersCount()})</span>
                )}
              </button>
            </div>

            <div className="search-controls-right">
              <SortOptions
                type={activeTab === 'projects' ? 'projects' : 'units'}
                sortBy={searchSort[activeTab]?.sortBy || 'created_at'}
                sortOrder={searchSort[activeTab]?.sortOrder || 'desc'}
                onSortChange={async (newSortBy, newSortOrder) => {
                  if (onSortChange) {
                    setIsApplyingFilters(true);
                    try {
                      await onSortChange(activeTab, newSortBy, newSortOrder);
                    } finally {
                      setIsApplyingFilters(false);
                    }
                  }
                }}
                className="inline-sort-options"
              />
            </div>
          </div> */}

            {/* Content Area */}
            <div
              className={`search-results-content ${
                isLoading || isApplyingFilters || favoritesLoading
                  ? "search-results-loading"
                  : ""
              }`}
            >
              {isLoading || favoritesLoading ? (
                <div className="search-loading">
                  <FaSpinner className="loading-spinner" />
                  <p>
                    {isFavoritesMode
                      ? language === "ar"
                        ? "جاري تحميل المفضلة..."
                        : "Loading favorites..."
                      : language === "ar"
                      ? "جاري البحث..."
                      : "Searching..."}
                  </p>
                </div>
              ) : favoritesError ? (
                <div className="search-loading">
                  <p>{favoritesError}</p>
                  <button onClick={loadFavorites} className="retry-btn">
                    {language === "ar" ? "إعادة المحاولة" : "Retry"}
                  </button>
                </div>
              ) : (
                <>
                  {activeTab === "projects" && (
                    <div
                      className={`results-list ${
                        isApplyingFilters ? "loading" : ""
                      }`}
                    >
                      {projects.length > 0 ? (
                        projects.map(renderProjectCard)
                      ) : (
                        <div className="no-results">
                          <FaBuilding className="no-results-icon" />
                          <p>
                            {language === "ar"
                              ? "لا توجد مشاريع"
                              : "No projects found"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "units" && (
                    <div
                      className={`results-list ${
                        isApplyingFilters ? "loading" : ""
                      }`}
                    >
                      {units.length > 0 ? (
                        units.map(renderUnitCard)
                      ) : (
                        <div className="no-results">
                          {isFavoritesMode ? (
                            <>
                              <HeartIcon
                                filled={false}
                                size={48}
                                color="#d1d5db"
                                className="no-results-icon"
                              />
                              <p>
                                {language === "ar"
                                  ? "لا توجد وحدات مفضلة"
                                  : "No favorite units"}
                              </p>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#6b7280",
                                  marginTop: "8px",
                                }}
                              >
                                {language === "ar"
                                  ? "ابدأ بإضافة وحدات للمفضلة لتظهر هنا"
                                  : "Start adding units to favorites to see them here"}
                              </p>
                            </>
                          ) : (
                            <>
                              <FaHome className="no-results-icon" />
                              <p>
                                {language === "ar"
                                  ? "لا توجد وحدات"
                                  : "No units found"}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "developers" && (
                    <div
                      className={`results-list ${
                        isApplyingFilters ? "loading" : ""
                      }`}
                    >
                      {developers.length > 0 ? (
                        developers.map(renderDeveloperCard)
                      ) : (
                        <div className="no-results">
                          <FaUser className="no-results-icon" />
                          <p>
                            {language === "ar"
                              ? "لا يوجد مطورين"
                              : "No developers found"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pagination */}
                  {!isLoading && pagination && pagination[activeTab] && (
                    <Pagination
                      currentPage={pagination[activeTab].current_page}
                      totalPages={pagination[activeTab].total_pages}
                      totalItems={pagination[activeTab].total}
                      itemsPerPage={pagination[activeTab].per_page}
                      onPageChange={(page) =>
                        onPageChange && onPageChange(activeTab, page)
                      }
                      onItemsPerPageChange={(perPage) =>
                        onItemsPerPageChange &&
                        onItemsPerPageChange(activeTab, perPage)
                      }
                      showItemsPerPage={true}
                      showInfo={true}
                      disabled={isLoading}
                      className="search-results-pagination"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Filter Dialog */}
        <FilterDialog
          isOpen={showFilterDialog}
          onClose={() => setShowFilterDialog(false)}
          type={activeTab === "projects" ? "projects" : "units"}
          filters={filters[activeTab] || {}}
          onApplyFilters={async ({ filters: newFilters }) => {
            setIsApplyingFilters(true);
            try {
              // Apply filters
              if (onFiltersChange) {
                await onFiltersChange(activeTab, newFilters);
              }
              setShowFilterDialog(false);
            } catch (error) {
              console.error("Error applying filters:", error);
            } finally {
              setIsApplyingFilters(false);
            }
          }}
          isLoading={isApplyingFilters}
        />
      </div>
    );
  }
);

SearchResults.displayName = "SearchResults";

export default SearchResults;
